import { XMarkIcon, PlusIcon, MinusIcon } from "@heroicons/react/16/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";
import Button from "./Button";
import combineClassName from "./utils/combineClassName";
import Tooltip from "./Tooltip";

interface CardProps {
  title?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  onClose?: () => void;
  disabled?: boolean;
  color?: string;
  className?: string;
  minimizable?: boolean;
  initialMinimized?: boolean;
  onClickTitle?: () => void;
  titleTooltip?: string;
}

export default function Card({
  title,
  children,
  footer,
  onClose,
  disabled = false,
  color = "white",
  className,
  minimizable = false,
  initialMinimized = false,
  onClickTitle,
  titleTooltip,
}: CardProps) {
  const [isMinimized, setIsMinimized] = useState(initialMinimized);

  return (
    <div
      className={combineClassName([
        "flex flex-col py-2 px-1 md:p-3 w-full rounded-md shadow-md border",
        disabled ? "bg-[#E0E0E0]" : "bg-white",
        className,
      ])}
      style={{ backgroundColor: color }}
    >
      <div className="flex flex-row justify-between items-center">
        {title && (
          <div
            className={`flex-1 flex flex-row items-center gap-2 mb-2 ${onClickTitle ? "border rounded-md px-2 py-1 cursor-pointer hover:border-electrum-300" : ""}`}
            onClick={onClickTitle}
          >
            <div className="text-md font-semibold">{title}</div>
            {titleTooltip && (
              <Tooltip
                text={titleTooltip}
                position="right"
                isActive={true}
                icon={
                  <InformationCircleIcon className="h-4 w-4 text-gray-600" />
                }
              />
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          {minimizable && (
            <Button
              variant="alternative"
              size="sm"
              onClick={() => setIsMinimized(!isMinimized)}
            >
              {isMinimized ? (
                <PlusIcon className="h-5 w-5" />
              ) : (
                <MinusIcon className="h-5 w-5" />
              )}
            </Button>
          )}
          {onClose && !disabled && (
            <Button variant="alternative" size="sm" onClick={onClose}>
              <XMarkIcon color="black" className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>
      {(!isMinimized || !minimizable) && (
        <>
          {children && <div className="flex-grow">{children}</div>}
          {footer && <div className="text-sm">{footer}</div>}
        </>
      )}
    </div>
  );
}
